import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useVersion, useVersions } from "@/hooks/useVersion";
import { useVersionStore } from "@/stores/versionStore";
import { CheckCircle2, Plus, ChevronDown, Eye, MapPin, Copy, Download, Settings } from "lucide-react";
import type { UniversalHeaderProps } from "../types";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { VersionForm } from './VersionForm';
import { VersionPublish } from './VersionPublish';
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useState } from "react";
import CopyChannelsModal from "@/features/regions/components/CopyChannelsModal";
import { useStaticFeaturesExport } from "@/hooks/useStaticFeaturesExport";
import { useOptimizedExport } from "@/hooks/useOptimizedExport";

export function UniversalHeader({ providerId, versionId }: UniversalHeaderProps) {
  const navigate = useNavigate();
  const { data: version, isLoading } = useVersion({ providerId, versionId });
  const { data: versions = [] } = useVersions(providerId, version?.region_id);
  const { openCreateModal, openPublishModal, setSelectedVersion } = useVersionStore();
  const [isCopyModalOpen, setIsCopyModalOpen] = useState(false);
  const { exportStaticFeatures, isExporting: isExportingStatic } = useStaticFeaturesExport();
  const { exportOptimizedData, isExporting: isExportingOptimized } = useOptimizedExport();
  
  // Fetch provider data
  const { data: provider } = useQuery({
    queryKey: ['provider', providerId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('providers')
        .select('id, name')
        .eq('id', providerId)
        .single();
      
      if (error) throw error;
      return data;
    },
    enabled: !!providerId,
  });

  // Fetch region data if version has a region_id
  const { data: region } = useQuery({
    queryKey: ['version-region', version?.region_id],
    queryFn: async () => {
      if (!version?.region_id) return null;
      const { data, error } = await supabase
        .from('regions')
        .select('id, name, zip_code')
        .eq('id', version.region_id)
        .single();
      
      if (error) throw error;
      return data;
    },
    enabled: !!version?.region_id,
  });

  const handleVersionChange = (newVersionId: number) => {
    // Look up the selected version to check if it has a region_id
    const selectedVersion = versions.find(v => v.id === newVersionId);
    
    if (selectedVersion?.region_id) {
      navigate(`/providers/${providerId}/regions/${selectedVersion.region_id}/versions/${newVersionId}/dashboard`);
    } else {
      navigate(`/providers/${providerId}/versions/${newVersionId}/dashboard`);
    }
  };

  const handlePublish = () => {
    if (!version) return;
    
    // Check if there's already a draft version in the same context
    let hasDraft = false;
    
    if (version.region_id) {
      // If we're in a region context, only check for drafts in this region
      hasDraft = versions.some(v => 
        v.status === 'draft' && 
        v.id !== version.id && 
        v.region_id === version.region_id
      );
    } else {
      // If we're not in a region context, check for any non-region drafts
      hasDraft = versions.some(v => 
        v.status === 'draft' && 
        v.id !== version.id && 
        !v.region_id
      );
    }
    
    if (hasDraft) {
      toast.error("Another draft version already exists. Please publish or delete it first.");
      return;
    }

    setSelectedVersion(version);
    openPublishModal();
  };

  const handleCreate = () => {
    // Check if there's already a draft version for the current region context
    let hasDraft = false;
    
    if (version?.region_id) {
      // If we're in a region context, only check for drafts in this region
      hasDraft = versions.some(v => v.status === 'draft' && v.region_id === version.region_id);
    } else {
      // If we're not in a region context, check for any drafts
      hasDraft = versions.some(v => v.status === 'draft');
    }
    
    if (hasDraft) {
      toast.error("A draft version already exists. Please publish or delete it first.");
      return;
    }

    openCreateModal();
  };

  const handlePreview = () => {
    if (version?.status === 'published') {
      if (version.region_id) {
        navigate(`/providers/${providerId}/regions/${version.region_id}/versions/${versionId}/preview`);
      } else {
        navigate(`/providers/${providerId}/versions/${versionId}/preview`);
      }
    }
  };
  
  const handleGoToRegion = () => {
    if (version?.region_id) {
      navigate(`/providers/${providerId}/regions/${version.region_id}/versions`);
    }
  };

  const handleOpenCopyModal = () => {
    setIsCopyModalOpen(true);
  };


  const handleStaticFeaturesExport = () => {
    if (!version || !provider || version.status !== 'published') return;
    
    exportStaticFeatures(
      providerId,
      versionId,
      provider.name,
      version.version_number
    );
  };

  const handleOptimizedExport = () => {
    if (!version || !provider || version.status !== 'published') return;
    
    exportOptimizedData(
      providerId,
      versionId,
      provider.name,
      version.version_number
    );
  };

  return (
    <>
      <div className="flex items-center justify-between mb-6">
        {/* Version Info Section */}
        <div className="space-y-1">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="outline" 
                    className="flex items-center gap-2"
                  >
                    <h2 className="text-2xl font-bold tracking-tight">
                      {isLoading ? (
                        <Skeleton className="h-8 w-[200px]" />
                      ) : (
                        `Version ${version?.version_number}`
                      )}
                    </h2>
                    <ChevronDown className="h-4 w-4 opacity-50" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-[200px]">
                  {versions.map((v) => (
                    <DropdownMenuItem
                      key={v.id}
                      onClick={() => handleVersionChange(v.id)}
                      className="flex items-center justify-between"
                    >
                      <span>Version {v.version_number}</span>
                      {v.status === 'draft' ? (
                        <Badge variant="secondary" className="ml-2">Draft</Badge>
                      ) : (
                        <Badge 
                          variant="default" 
                          className={cn(
                            "ml-2",
                            "bg-green-500 hover:bg-green-600",
                            "text-white hover:text-white"
                          )}
                        >
                          Published
                        </Badge>
                      )}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            {version?.status === 'draft' ? (
              <Badge variant="secondary">Draft</Badge>
            ) : (
              <Badge 
                variant="default" 
                className={cn(
                  "bg-green-500 hover:bg-green-600",
                  "text-white hover:text-white"
                )}
              >
                Published
              </Badge>
            )}
            
            {/* Region information */}
            {version?.region_id && region && (
              <Button 
                variant="ghost" 
                size="sm"
                className="flex items-center gap-1 text-muted-foreground hover:text-foreground"
                onClick={handleGoToRegion}
              >
                <MapPin className="h-4 w-4" />
                <span>
                  Region: {region.name}
                  {region.zip_code && ` (${region.zip_code})`}
                </span>
              </Button>
            )}
          </div>
          <div className="text-muted-foreground">
            {isLoading ? (
              <Skeleton className="h-4 w-[300px]" />
            ) : (
              version?.description || "No description provided"
            )}
          </div>
        </div>

        {/* Actions Section */}
        <div className="flex items-center gap-2">
          {/* Copy Channels button - shown only for versions with region_id */}
          {version?.region_id && (
            <Button 
              variant="outline" 
              onClick={handleOpenCopyModal}
            >
              <Copy className="h-4 w-4 mr-2" />
              Copy Channels
            </Button>
          )}

          {/* Only show preview button for published versions */}
          {version?.status === 'published' && (
            <Button 
              variant="outline" 
              onClick={handlePreview}
            >
              <Eye className="h-4 w-4 mr-2" />
              Preview
            </Button>
          )}

          {/* Only show create new version for regions if we're in a region context */}
          <Button 
            variant="outline" 
            onClick={handleCreate}
          >
            <Plus className="h-4 w-4 mr-2" />
            New Version
          </Button>

          {/* Only show publish option for draft versions */}
          {version?.status === 'draft' && (
            <Button 
              onClick={handlePublish}
            >
              <CheckCircle2 className="h-4 w-4 mr-2" />
              Publish
            </Button>
          )}

          {/* Only show download options for published versions */}
          {version?.status === 'published' && (
            <>
              <Button 
                variant="outline"
                onClick={handleOptimizedExport}
                disabled={isExportingOptimized}
                title="Download optimized JSON with smart field optimization and embedded references"
              >
                <Download className="h-4 w-4 mr-2" />
                {isExportingOptimized ? 'Exporting...' : 'Download JSON'}
              </Button>
              
              <Button 
                variant="outline"
                onClick={handleStaticFeaturesExport}
                disabled={isExportingStatic}
                title="Download provider static features and business info"
              >
                <Settings className="h-4 w-4 mr-2" />
                {isExportingStatic ? 'Exporting...' : 'Download Static'}
              </Button>
            </>
          )}
        </div>
      </div>
      
      {/* Add the modal components */}
      <VersionForm />
      <VersionPublish />
      
      {/* Copy Channels Modal */}
      {isCopyModalOpen && (
        <CopyChannelsModal
          isOpen={isCopyModalOpen}
          onClose={() => setIsCopyModalOpen(false)}
          providerId={providerId}
          currentVersionId={versionId}
          currentRegionId={version?.region_id}
        />
      )}
    </>
  );
} 